import { useState, useMemo } from 'react';
import { PlusIcon } from '@interstate/components/Icons';
import { Action } from '@interstate/components/Action';
import { useCustomerCardContext } from '../../CustomerFormContext';
import EmploymentFields from './EmploymentFields';
import { ExpandedSectionCard } from './housingAndEmployment.styles';
import CustomerFieldsLayoutHeader from './CustomerFieldsLayoutHeader';
import { hasEmploymentData } from './housingAndEmployment.utils';
import { DEFAULT_ERROR_STATE, type ErrorState } from '@makemydeal/dr-dash-ui-utils';
import { CUSTOMER_CARD_ANALYTICS } from '../../../analytics';

export const PreviousEmployment = () => {
    const {
        formValues,
        formErrors,
        handleInputChange,
        handleNumberInputChange,
        handleInitPreviousEmployment,
        handleClearPreviousEmployment,
        isLocked,
        customerRole,
        onAnalyticsEvent
    } = useCustomerCardContext();

    const [expanded, setExpanded] = useState(() =>
        hasEmploymentData(formValues.previousEmployment as Record<string, unknown> | undefined)
    );

    const prevEmp = formValues.previousEmployment;
    const prevEmpErrors = formErrors?.previousEmployment as Record<string, ErrorState> | undefined;

    const employmentValues = useMemo(
        () => ({
            status: prevEmp?.status,
            employerName: prevEmp?.employerName,
            occupation: prevEmp?.occupation,
            workPhone: prevEmp?.workPhone,
            totalYearsEmployed: prevEmp?.totalYearsEmployed,
            totalMonthsEmployed: prevEmp?.totalMonthsEmployed
        }),
        [prevEmp]
    );

    const employmentErrors = useMemo(
        () => ({
            status: prevEmpErrors?.status || DEFAULT_ERROR_STATE,
            employerName: prevEmpErrors?.employerName || DEFAULT_ERROR_STATE,
            occupation: prevEmpErrors?.occupation || DEFAULT_ERROR_STATE,
            workPhone: prevEmpErrors?.workPhone || DEFAULT_ERROR_STATE,
            totalYearsEmployed: prevEmpErrors?.totalYearsEmployed || DEFAULT_ERROR_STATE,
            totalMonthsEmployed: prevEmpErrors?.totalMonthsEmployed || DEFAULT_ERROR_STATE
        }),
        [prevEmpErrors]
    );

    const handleDelete = () => {
        handleClearPreviousEmployment();
        setExpanded(false);
    };

    return (
        <div data-testid="previous-employment-section">
            {!expanded ? (
                <Action
                    data-testid="previous-employment-button"
                    actionIcon={{ icon: <PlusIcon />, start: true }}
                    sx={{ marginTop: '1rem' }}
                    onClick={() => {
                        onAnalyticsEvent?.(CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_PREVIOUS_EMPLOYMENT, { customerRole });
                        handleInitPreviousEmployment();
                        setExpanded(true);
                    }}
                >
                    Add Previous Employment
                </Action>
            ) : (
                <ExpandedSectionCard>
                    <CustomerFieldsLayoutHeader
                        onDelete={handleDelete}
                        deleteTestId="previous-employment-delete-button"
                        disabled={isLocked}
                    >
                        Previous Employment
                    </CustomerFieldsLayoutHeader>
                    <EmploymentFields
                        type="previous"
                        values={employmentValues}
                        errors={employmentErrors}
                        handleInputChange={handleInputChange}
                        handleNumberInputChange={handleNumberInputChange}
                        disabled={isLocked}
                    />
                </ExpandedSectionCard>
            )}
        </div>
    );
};

export default PreviousEmployment;
